import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/*
 * RES-1336 — the add-on flow's modal header, described by the step that is on screen.
 *
 * The problem this solves: the flow is three screens inside ONE wrapper, and the wrapper drew one
 * fixed header for all of them. On desktop that produced two back controls a few pixels apart going
 * to different places (the wrapper's, to the receipt; the questions screen's own, to the picker), and
 * on mobile it produced none at all on two of the three screens. Neither matches the design, where
 * every step carries exactly one control on the left and its own centred title.
 *
 * ⛔ THIS IS NOT THE STEP TAKING AN OPINION ON ITS PRESENTATION. `BookingAddOnsPage` still does not
 * know whether it is a sheet, a modal or a flat page — it only states *where back goes from here*, and
 * the wrapper decides how to draw that. Which is the same division the questions screen already used
 * when it drew its own chevron; this just moves the drawing to the component that owns the header.
 *
 * ⚠️ `withLeftArrowButton` is deliberately NOT the mechanism. On `ReceiptBooking` that prop means
 * "leave the cancel-booking sub-step", and it is read at all nine places `PaymentContent` is mounted.
 * This context is provided ONLY on the `add-on-request` route, so every other route keeps the exact
 * props and behaviour it has today.
 */
export interface StepHeader {
  /** Centred title the wrapper renders. */
  title?: string;
  /**
   * The step's own content already renders this title as its heading, so the wrapper NAMES the dialog
   * with it but does not paint the header band — otherwise the tenant reads the same words twice, a few
   * pixels apart.
   *
   * ⚠️ Not the same as omitting `title`. Omitting it leaves the dialog with no accessible name at all;
   * this keeps the name and drops only the duplicate paint. The confirmation step sets it because
   * `AddOnsOrdered` renders the SAME message id as a focus-managed heading — it is the screen's real
   * title and the focus target, so the content's copy is the one that has to survive.
   */
  titleOwnedByContent?: boolean;
  /**
   * Set only when this step has a step ABOVE it to return to — the wrapper then draws a back arrow
   * instead of the exit X. Absent on the first and last screens, which exit rather than go back.
   */
  onBack?: VoidFunction;
  /**
   * Where the exit control goes when there is no `onBack`. Absent falls through to the wrapper's own
   * `onClose`, so a step that does not care keeps today's behaviour.
   */
  onClose?: VoidFunction;
  /**
   * Whether the shell should wrap this step in a desktop modal. Declared per step because one shell
   * now serves the receipt and the add-on step: the receipt's bodies bring their own (a two-pane
   * layout the shell cannot provide), the add-on step's do not. Absent falls through to the shell's
   * own prop.
   */
  withDesktopModal?: boolean;
}

interface StepHeaderValue {
  header: StepHeader;
  setHeader: (header: StepHeader) => void;
}

const StepHeaderContext = createContext<StepHeaderValue | null>(null);

export const StepHeaderProvider = ({ children }: PropsWithChildren<unknown>): JSX.Element => {
  const [header, setHeaderState] = useState<StepHeader>({});

  /*
   * Bails out when nothing changed. The publishing effect below re-runs whenever a handler's identity
   * changes, and both of today's handlers are stable — but a future one built from a fresh object each
   * render would otherwise set state on every render and spin. Cheap insurance against a loop that
   * would present as a hung screen rather than an error.
   */
  const setHeader = useCallback((next: StepHeader) => {
    setHeaderState(prev =>
      prev.title === next.title &&
      prev.onBack === next.onBack &&
      prev.onClose === next.onClose &&
      prev.withDesktopModal === next.withDesktopModal &&
      prev.titleOwnedByContent === next.titleOwnedByContent
        ? prev
        : next,
    );
  }, []);

  const value = useMemo(() => ({ header, setHeader }), [header, setHeader]);

  return <StepHeaderContext.Provider value={value}>{children}</StepHeaderContext.Provider>;
};

/** The header the step asked for. `{}` outside a provider, which is every route but `add-on-request`. */
export const useStepHeader = (): StepHeader => useContext(StepHeaderContext)?.header ?? {};

/**
 * Declare this step's header. A no-op outside a provider, so the page stays renderable on its own —
 * storybook and the specs mount it bare.
 */
export const useSetStepHeader = ({
  title,
  onBack,
  onClose,
  withDesktopModal,
  titleOwnedByContent,
}: StepHeader): void => {
  const setHeader = useContext(StepHeaderContext)?.setHeader;

  useEffect(() => {
    setHeader?.({ title, onBack, onClose, withDesktopModal, titleOwnedByContent });
  }, [setHeader, title, onBack, onClose, withDesktopModal, titleOwnedByContent]);

  /*
   * ⛔ CLEARED ON UNMOUNT, and nothing works without this. The provider outlives the step — that is the
   * point of it, so the sheet survives the route change — so a header left behind is inherited by
   * whatever renders next. Leaving the add-on step handed the receipt a back arrow pointing at the
   * add-ons and the title "Add-ons" above its own content.
   *
   * ⚠️ Its own effect, keyed on the stable setter rather than on the values. Putting the cleanup on
   * the effect above would clear and re-set on every title or handler change — a visible flicker
   * through the X on each step transition.
   */
  useEffect(() => () => setHeader?.({}), [setHeader]);
};
